/**
 * Format date in Central Time (CT) timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Formatting options
 * @returns {string} - Formatted date string in CT timezone
 */
export const formatDateCT = (dateString, options = {}) => {
  if (!dateString) return '—';

  const date = new Date(dateString);

  // Verify the date is valid
  if (isNaN(date.getTime())) {
    console.error('Invalid date:', dateString);
    return '—';
  }

  const defaultOptions = {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  const formatted = date.toLocaleString('en-US', defaultOptions);
  return formatted;
};

/**
 * Format date and time in Central Time (CT) timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} - Formatted date and time string in CT timezone
 */
export const formatDateTimeCT = (dateString) => {
  return formatDateCT(dateString, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }) + ' CT';
};

/**
 * Format date only (no time) in Central Time (CT) timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} - Formatted date string in CT timezone
 */
export const formatDateOnlyCT = (dateString) => {
  return formatDateCT(dateString);
};

/**
 * Get month string in format "YYYY-MM" from a date
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} - Month string in "YYYY-MM" format
 */
export const getMonthString = (dateString) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  // Convert to CT timezone
  const ctDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const year = ctDate.getFullYear();
  const month = String(ctDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Format month string for display (e.g., "January 2024")
 * @param {string} monthString - Month string in "YYYY-MM" format
 * @returns {string} - Formatted month string (e.g., "January 2024")
 */
export const formatMonthDisplay = (monthString) => {
  if (!monthString) return '';

  const [year, month] = monthString.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  
  return date.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Get all unique months from an array of posts
 * @param {Array} posts - Array of post objects with posted_at field
 * @returns {Array} - Sorted array of unique month strings in "YYYY-MM" format
 */
export const getUniqueMonths = (posts) => {
  if (!posts || !Array.isArray(posts)) return [];

  const months = new Set();
  posts.forEach(post => {
    if (post.posted_at) {
      const monthString = getMonthString(post.posted_at);
      if (monthString) {
        months.add(monthString);
      }
    }
  });

  // Sort descending (newest first)
  return Array.from(months).sort().reverse();
};

